import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import OAuthConsentPage from '@/components/oauth/OAuthConsentPage';
import type { AuthorizationRequest } from '@/client';
import { searchWithRequestId } from '@/utils/oauth/consentUtils';

/**
 * The client's own parameters, and the request id the page stamps on once it has one.
 *
 * Every name is RFC 6749's or RFC 7636's, spelled the way the client wrote it into the address:
 * the page passes the whole set through without interpreting any of it, and the API validates it.
 */
interface ConsentSearch extends AuthorizationRequest {
	request_id?: string;
}

/**
 * A parameter the client wrote, or nothing at all.
 *
 * Nothing rather than an empty string: the router writes the validated search back onto the
 * address, so a parameter normalised to '' is spelled out there — `?code_challenge=` — on a
 * request that never carried one. The API defaults every field of `AuthorizationRequest` to
 * empty itself, so leaving it out asks exactly the same question and is refused the same way.
 */
const asText = (value: unknown): string | undefined => (typeof value === 'string' && value !== '' ? value : undefined);

const Consent = () => {
	const navigate = useNavigate();
	const { request_id, ...params } = Route.useSearch();

	const stampRequestId = useCallback(
		(requestId: string) => {
			void navigate({
				to: '.',
				search: (previous) => searchWithRequestId(previous as Record<string, unknown>, requestId),
				replace: true,
			});
		},
		[navigate],
	);

	return <OAuthConsentPage params={params} requestId={request_id} onBegun={stampRequestId} />;
};

export const Route = createFileRoute({
	component: Consent,
	validateSearch: (search: Record<string, unknown>): ConsentSearch => ({
		client_id: asText(search.client_id),
		redirect_uri: asText(search.redirect_uri),
		response_type: asText(search.response_type),
		code_challenge: asText(search.code_challenge),
		code_challenge_method: asText(search.code_challenge_method),
		resource: asText(search.resource),
		state: asText(search.state),
		scope: asText(search.scope),
		request_id: asText(search.request_id),
	}),
});
